import React from "react";
import styled from "styled-components";
import { Container } from "react-bootstrap";

const Section = styled.section`
  padding: 32px 0 48px;
  background: var(--nd-black);
`;

const Card = styled.div`
  background: var(--nd-surface);
  border: 1px solid var(--nd-border);
  border-radius: 12px;
  padding: 32px;
  @media (max-width: 768px) { padding: 24px; }
`;

const FactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const FactCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FactLabel = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
`;

const FactValueDoto = styled.span`
  font-family: 'Doto', 'Space Mono', monospace;
  font-size: 36px;
  font-weight: 700;
  color: var(--nd-text-display);
  line-height: 1;
  letter-spacing: -0.02em;
`;

const FactValue = styled.span`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  color: var(--nd-text-primary);
`;

const FactSub = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  color: var(--nd-text-secondary);
  letter-spacing: 0.04em;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-primary);
  border: 1px solid var(--nd-border-visible);
  padding: 6px 10px;
  border-radius: 999px;
`;

const SubHeading = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: var(--nd-text-display);
  letter-spacing: -0.01em;
  margin: 32px 0 12px;
`;

const Bullets = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const Bullet = styled.li`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 15px;
  color: var(--nd-text-secondary);
  line-height: 1.6;
  padding-left: 20px;
  position: relative;
  margin-bottom: 8px;
  &::before {
    content: "·";
    position: absolute;
    left: 4px;
    color: var(--nd-text-disabled);
  }
`;

const formatStartDate = (iso) => {
  if (!iso) return "TBD";
  try {
    const d = new Date(`${iso}T00:00:00Z`);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch (_) {
    return iso;
  }
};

const ProjectFacts = ({ opening }) => (
  <Section>
    <Container>
      <Card>
        <FactGrid>
          <FactCell>
            <FactLabel>Duration</FactLabel>
            <FactValueDoto>{opening.durationMonths}mo</FactValueDoto>
          </FactCell>
          <FactCell>
            <FactLabel>Start date</FactLabel>
            <FactValue>{formatStartDate(opening.startDate)}</FactValue>
          </FactCell>
          <FactCell>
            <FactLabel>Location</FactLabel>
            <FactValue>{opening.location}</FactValue>
            {opening.locationDetail && <FactSub>{opening.locationDetail}</FactSub>}
          </FactCell>
          <FactCell>
            <FactLabel>Status</FactLabel>
            <FactValue>{opening.status === "open" ? "Accepting applications" : opening.status}</FactValue>
          </FactCell>
        </FactGrid>

        <FactLabel>Tech stack</FactLabel>
        <div style={{ marginTop: 8 }}>
          <ChipRow>
            {(opening.techStack || []).map((t) => (
              <Chip key={t}>{t}</Chip>
            ))}
          </ChipRow>
        </div>

        {opening.responsibilities && opening.responsibilities.length > 0 && (
          <>
            <SubHeading>What you will own</SubHeading>
            <Bullets>
              {opening.responsibilities.map((r, i) => (
                <Bullet key={i}>{r}</Bullet>
              ))}
            </Bullets>
          </>
        )}

        {opening.requirements && opening.requirements.length > 0 && (
          <>
            <SubHeading>What we are looking for</SubHeading>
            <Bullets>
              {opening.requirements.map((r, i) => (
                <Bullet key={i}>{r}</Bullet>
              ))}
            </Bullets>
          </>
        )}
      </Card>
    </Container>
  </Section>
);

export default ProjectFacts;
